/**
 * MonthNavigator — lewa strzałka, miesiąc + rok (np. "Maj 2026"), prawa strzałka.
 * Pod spodem 3 stat cards: Przychody (emerald), Wydatki (rose), Saldo (auto).
 */
import { Icons } from '../Icons';
import { formatCurrency, formatMonth, addMonths } from '../../lib/utils';
import type { MonthSummary } from '../../features/transactions/selectors';

interface Props {
  month: Date;
  onChange: (d: Date) => void;
  summary: MonthSummary;
}

export function MonthNavigator({ month, onChange, summary }: Props) {
  const isCurrentMonth = (() => {
    const now = new Date();
    return now.getFullYear() === month.getFullYear() && now.getMonth() === month.getMonth();
  })();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onChange(addMonths(month, -1))}
          aria-label="Poprzedni miesiąc"
          className="w-11 h-11 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <Icons.ChevronLeft size={20} />
        </button>
        <div className="text-center">
          <p className="text-base font-bold text-slate-900 dark:text-white">{formatMonth(month)}</p>
          {isCurrentMonth && (
            <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wide font-bold text-sky-600 dark:text-sky-300">
              Bieżący
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => onChange(addMonths(month, 1))}
          aria-label="Następny miesiąc"
          disabled={isCurrentMonth}
          className="w-11 h-11 flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
        >
          <Icons.ChevronRight size={20} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatCard label="Przychody" value={summary.income} tone="income" />
        <StatCard label="Wydatki" value={summary.expense} tone="expense" />
        <div className="col-span-2 sm:col-span-1">
          <StatCard label="Saldo" value={summary.balance} tone={summary.balance >= 0 ? 'neutral' : 'expense'} />
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'income' | 'expense' | 'neutral';
}) {
  const valueColor =
    tone === 'income'
      ? 'text-emerald-600 dark:text-emerald-300'
      : tone === 'expense'
      ? 'text-rose-600 dark:text-rose-300'
      : 'text-slate-900 dark:text-white';
  const sign = tone === 'income' ? '+' : tone === 'expense' ? '-' : value >= 0 ? '+' : '';
  const display = `${sign}${formatCurrency(Math.abs(value)).replace('-', '')}`;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-3">
      <p className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 font-bold">
        {label}
      </p>
      <p className={`text-base sm:text-lg font-bold tabular-nums leading-none mt-1 truncate ${valueColor}`}>
        {display}
      </p>
    </div>
  );
}
